import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Server, Puzzle, RefreshCw, KeyRound, ExternalLink, CheckCircle2, AlertTriangle, Clock, Loader2,
} from 'lucide-react';
import { TENDER_SOURCES, SERVER_SOURCE_KEYS, COLLECTOR_EXTENSION_ID } from '../../components/tenderSourceCatalog';
import TenderRetrievalGuide from '../../components/TenderRetrievalGuide';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/*
 * WO-32 — Tender Sources, rebuilt around the only two ways tenders arrive:
 *   1. SERVER: three portals the Mid2Bid server reads by itself (one button).
 *   2. COLLECTOR: every other portal, imported by an admin with the Mid2Bid
 *      Collector Chrome add-on. This page gives the add-on its key and shows,
 *      per portal, how many open tenders Mid2Bid holds and when it was last collected.
 * A request that fails is shown as failed — never as zero.
 */

/*
 * Talk to the Mid2Bid Collector add-on installed in THIS Chrome. Chrome only lets a
 * page do this when the add-on lists the page's address (its "externally_connectable"
 * setting), so on any other browser or without the add-on the answer is simply null.
 */
function askAddon(message, timeoutMs = 8000) {
  return new Promise((resolve) => {
    const rt = window.chrome && window.chrome.runtime;
    if (!rt || !rt.sendMessage) { resolve(null); return; }
    const timer = setTimeout(() => resolve(null), timeoutMs);
    try {
      rt.sendMessage(COLLECTOR_EXTENSION_ID, message, (reply) => {
        clearTimeout(timer);
        // lastError must be read, or Chrome logs it; it means "not installed".
        resolve(rt.lastError ? null : (reply || null));
      });
    } catch (_) { clearTimeout(timer); resolve(null); }
  });
}

const fmtDate = (v) => (v ? new Date(v).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

export default function AdminTenderSourcesPage() {
  const { getToken } = useAuth();
  const auth = useCallback(() => ({ Authorization: `Bearer ${getToken()}` }), [getToken]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [health, setHealth] = useState({});
  const [collector, setCollector] = useState(null);
  const [job, setJob] = useState(null);
  const [addon, setAddon] = useState(undefined);   // undefined = checking, null = not installed here
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [h, c, a] = await Promise.all([
        fetch(`${API_URL}/api/intelligence/tenders/scrapers/health`, { headers: auth() }),
        fetch(`${API_URL}/api/admin/collector/status`, { headers: auth() }),
        fetch(`${API_URL}/api/intelligence/tenders/ingest-job/active`, { headers: auth() }),
      ]);
      if (!h.ok || !c.ok || !a.ok) throw new Error(`HTTP ${[h, c, a].find((r) => !r.ok).status}`);
      const hj = await h.json();
      const map = {};
      (hj.sources || []).forEach((s) => { map[s.key] = s; });
      setHealth(map);
      setCollector(await c.json());
      setJob((await a.json()).job || null);
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { askAddon({ type: 'm2b-hello' }, 1500).then(setAddon); }, []);

  // While a server refresh runs, follow it every 3 seconds; reload the page data when it ends.
  useEffect(() => {
    if (!job || !['queued', 'running'].includes(job.status)) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`${API_URL}/api/intelligence/tenders/ingest-job/${job._id}`, { headers: auth() });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        setJob(j);
        if (!['queued', 'running'].includes(j.status)) {
          clearInterval(pollRef.current);
          const t = j.totals || {};
          if (j.status === 'completed') toast.success(`Server refresh finished: ${t.created || 0} new, ${t.updated || 0} updated.`);
          else toast.error(`Server refresh ${j.status}.`);
          load();
        }
      } catch (e) {
        clearInterval(pollRef.current);
        toast.error(`Lost track of the refresh: ${e.message}`);
      }
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [job, auth, load]);

  const refreshServer = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/intelligence/tenders/ingest-start?source_keys=${encodeURIComponent(SERVER_SOURCE_KEYS.join(','))}`, { method: 'POST', headers: auth() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setJob({ _id: d.job_id, status: 'queued' });
      toast.success('Server refresh started for Abu Dhabi, Bahrain and Ashghal.');
    } catch (e) {
      toast.error(`Could not start the refresh: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  /*
   * Connect: create a key and hand it straight to the add-on. The key is never shown
   * on screen and never copied by hand. No add-on in this Chrome → no key is created.
   */
  const connect = async () => {
    setBusy(true);
    try {
      const hello = await askAddon({ type: 'm2b-hello' }, 1500);
      setAddon(hello);
      if (!hello || !hello.ok) {
        toast.error('The Mid2Bid Collector add-on is not installed in this Chrome. Install it, reload this page, and press Connect again.');
        return;
      }
      const r = await fetch(`${API_URL}/api/admin/collector/key`, { method: 'POST', headers: auth() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      const res = await askAddon({ type: 'm2b-set-key', key: d.collector_key, baseUrl: API_URL });
      if (!res) throw new Error('the add-on did not answer — reload this page and press Connect again');
      if (!res.ok) throw new Error(`the add-on could not use the key (${res.error || 'no reason given'})`);
      toast.success(`Collector connected as ${res.admin}.`);
      setAddon({ ...hello, has_key: true });
      load();
    } catch (e) {
      toast.error(`Could not connect the collector: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect the collector? The add-on will stop working until a new key is created.')) return;
    const r = await fetch(`${API_URL}/api/admin/collector/key`, { method: 'DELETE', headers: auth() });
    if (r.ok) { toast.success('Collector disconnected.'); load(); } else toast.error(`Could not disconnect: HTTP ${r.status}`);
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="admin-tender-sources-loading">
        <Skeleton className="h-8 w-80" />
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32" />)}
      </div>
    );
  }

  const serverSources = TENDER_SOURCES.filter((s) => s.method === 'server');
  const collectorSources = TENDER_SOURCES.filter((s) => s.method === 'collector');
  const running = job && ['queued', 'running'].includes(job.status);
  const key = collector?.key;
  const keyActive = key && !key.revoked;

  return (
    <div className="space-y-6" data-testid="admin-tender-sources-page">
      <div>
        <h1 className="text-2xl font-bold">Tender Sources</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Tenders arrive in two ways. Three portals are read by the Mid2Bid server itself. Every other portal
          is imported by an admin with the <b>Mid2Bid Collector</b> Chrome add-on, from the portal&apos;s public list.
          Nothing runs on a timer: a person starts every collection.
        </p>
      </div>

      <TenderRetrievalGuide />

      {loadError && (
        <Card className="border-red-300 bg-red-50 dark:bg-red-950/20" data-testid="tender-sources-load-error">
          <CardContent className="py-4 text-sm text-red-700 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> This page could not load its data ({loadError}). The figures below are not shown rather than shown as zero.
            <Button size="sm" variant="outline" className="ml-auto" onClick={load}>Try again</Button>
          </CardContent>
        </Card>
      )}

      {/* ---------------- 1. Server sources ---------------- */}
      <Card data-testid="server-sources-card">
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2"><Server className="w-5 h-5 text-emerald-600" /> Server sources</CardTitle>
            <CardDescription>Read by Mid2Bid itself, one after the other, in a single job.</CardDescription>
          </div>
          <Button onClick={refreshServer} disabled={busy || running} data-testid="refresh-server-sources-btn" className="gap-2 shrink-0">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {running ? 'Refreshing…' : 'Refresh server sources'}
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {running && job.current_source && (
            <div className="text-xs text-muted-foreground">
              Reading {job.current_source.name} ({job.current_source.index}/{job.current_source.total}) · {job.totals?.created || 0} new so far
            </div>
          )}
          {serverSources.map((s) => {
            const h = health[s.key];
            const state = h?.health || 'never_run';
            const good = state === 'healthy';
            return (
              <div key={s.key} className="flex flex-wrap items-center gap-3 p-2 rounded-md border" data-testid={`server-source-${s.key}`}>
                <span className="text-lg">{s.flag}</span>
                <span className="font-medium">{s.name}</span>
                <Badge variant="outline" className={good ? 'text-emerald-700 border-emerald-300' : state === 'never_run' ? '' : 'text-red-700 border-red-300'}>
                  {good ? <CheckCircle2 className="w-3 h-3 mr-1" /> : state === 'never_run' ? <Clock className="w-3 h-3 mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
                  {state.replace('_', ' ')}
                </Badge>
                <span className="text-xs text-muted-foreground">Last run: {fmtDate(h?.last_run_at)}{h?.last_found != null ? ` · ${h.last_found} found, ${h.last_created || 0} new` : ''}</span>
                {h?.last_error && <span className="text-xs text-red-700 w-full">{h.last_error}</span>}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* ---------------- 2. Collector add-on ---------------- */}
      <Card data-testid="collector-card">
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2"><Puzzle className="w-5 h-5 text-sky-600" /> Collector add-on</CardTitle>
            <CardDescription>
              {keyActive
                ? <>Connected · key created {fmtDate(key.created_at)} · last used {fmtDate(key.last_used_at)}</>
                : 'Not connected yet.'}
              <span className="block text-xs mt-0.5" data-testid="collector-addon-state">
                {addon === undefined ? 'Looking for the add-on in this Chrome…'
                  : addon ? `Add-on installed in this Chrome (version ${addon.version}).`
                    : 'Add-on not found in this Chrome — install it first (see INSTALL.md), then reload this page.'}
              </span>
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2 sm:shrink-0">
            <Button onClick={connect} disabled={busy} variant={keyActive ? 'outline' : 'default'} className="gap-2" data-testid="collector-connect-btn">
              <KeyRound className="w-4 h-4" /> {keyActive ? 'Reconnect' : 'Connect the collector'}
            </Button>
            {keyActive && <Button onClick={disconnect} variant="ghost" className="text-red-600" data-testid="collector-disconnect-btn">Disconnect</Button>}
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="collector-sources-table">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b">
                  <th className="py-2 pr-2">Portal</th>
                  <th className="py-2 pr-2">How the add-on reads it</th>
                  <th className="py-2 pr-2 text-right">Open</th>
                  <th className="py-2 pr-2 text-right">Stored</th>
                  <th className="py-2 pr-2">Last collected</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {collectorSources.map((s) => {
                  const st = collector?.sources?.[s.key];
                  return (
                    <tr key={s.key} className="border-b last:border-0" data-testid={`collector-source-${s.key}`}>
                      <td className="py-2 pr-2">
                        <span className="mr-2">{s.flag}</span><span className="font-medium">{s.name}</span>
                        {s.note && <Badge variant="outline" className="ml-2 text-[10px]">{s.note}</Badge>}
                      </td>
                      <td className="py-2 pr-2 text-xs text-muted-foreground">
                        {s.pages === 'auto' ? 'Reads the next pages by itself' : 'Page on screen only'}
                      </td>
                      <td className="py-2 pr-2 text-right">{st ? st.open : '—'}</td>
                      <td className="py-2 pr-2 text-right text-muted-foreground">{st ? st.total : '—'}</td>
                      <td className="py-2 pr-2 text-xs">
                        {fmtDate(st?.last_collected_at)}
                        {st?.enrichment_pending > 0 && <span className="block text-amber-700">{st.enrichment_pending} still being translated/classified</span>}
                        {st?.last_enrichment_error && <span className="block text-red-700">{st.last_enrichment_error}</span>}
                      </td>
                      <td className="py-2 text-right">
                        {/* "#m2b-collect" at the end of the link makes the add-on (1.1.1 or later) open its panel by itself. */}
                        <a href={`${s.url}#m2b-collect`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-sky-700 hover:underline" data-testid={`collector-open-${s.key}`}>
                          Open &amp; collect <ExternalLink className="w-3 h-3" />
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <ol className="mt-4 text-xs text-muted-foreground list-decimal pl-5 space-y-1">
            <li>Click <b>Open &amp; collect</b> on a portal&apos;s row: the portal opens with the Mid2Bid panel already open.</li>
            <li>Press <b>Read the list</b> (Dubai, Sharjah and the UAE federal portal read the page on screen straight away).</li>
            <li>Check the panel, press <b>Import</b>. Translation and industries are added in the background. More in the guide above.</li>
          </ol>
        </CardContent>
      </Card>

    </div>
  );
}
